import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarClock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Droplets,
  Link as LinkIcon,
  MapPin,
  Menu,
  Plane,
  Scale,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Users,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import axios from 'axios';
import MasterTourLogo from './MasterTourLogo';
import PlatformPreview from './PlatformPreview';
import SitePopup from './SitePopup';
import SubscribeForm from './SubscribeForm';

import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const iconMap = {
  'shopping-bag': ShoppingBag,
  plane: Plane,
  building: Building2,
  link: LinkIcon,
  'bar-chart': BarChart3,
  smartphone: Smartphone,
  'map-pin': MapPin,
  users: Users,
  clock: Clock,
  wrench: Wrench,
  droplets: Droplets,
  scale: Scale,
  zap: Zap,
  sparkles: Sparkles,
};

// Real operational imagery (replaces abstract AI graphics)
const HERO_BG = '/hopseat-coastal-bus.jpg';
const AUDIENCE_IMAGES = {
  drivers: '/hopseat-jet-bus.jpg',
  companies: '/hopseat-fleet-forum.jpg',
  tours: '/hopseat-farm-tour.jpg',
};
const AUDIENCE_LINKS = {
  drivers: '/for-drivers',
  companies: '/for-companies',
  tours: '/for-tours',
};

const HOW_IT_WORKS = [
  { icon: MapPin, title: 'Post your trip', desc: 'Enter the route, dates, and vehicle requirements in seconds — or set your availability if you drive.' },
  { icon: Zap, title: 'Get matched instantly', desc: 'We alert verified CDL drivers available in your region and surface qualified candidates fast.' },
  { icon: CheckCircle, title: 'Confirm & go', desc: 'Review credentials, accept the match, and hit the road. The show stays on schedule.' },
];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <div
    data-reveal
    className={`hsx-reveal ${className}`}
    style={delay ? { transitionDelay: `${delay}ms` } : undefined}
  >
    {children}
  </div>
);

const LandingExperience = () => {
  const { user, logout } = useAuth();
  const isPreview = new URLSearchParams(window.location.search).has('_preview');
  const effectiveUser = isPreview ? null : user;
  const [content, setContent] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.15 }
    );
    const targets = document.querySelectorAll('[data-reveal]');
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [content]);

  useEffect(() => {
    fetchContent();
    fetchPopupConfig();
    fetchNavPages();
    fetchStats();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || 0;
      const total = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      setScrollY(y);
      setScrollProgress(Math.min(100, (y / total) * 100));
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchNavPages = async () => {
    try {
      const res = await axios.get(`${API}/pages`);
      setNavPages((res.data.pages || []).filter((p) => p.show_in_nav));
    } catch (e) {
      setNavPages([]);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/landing-page-content`);
      if (res.data?.content) setContent(res.data.content);
    } catch (e) {
      setContent(null);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/public/landing-stats`);
      setStats(res.data || null);
    } catch (e) {
      setStats(null);
    }
  };

  const fetchPopupConfig = async () => {
    try {
      if (sessionStorage.getItem('popupDismissed') === 'true') return;
      const res = await axios.get(`${API}/popup-config`);
      if (res.data?.popup) {
        setTimeout(() => {
          if (!sessionStorage.getItem('popupDismissed')) {
            setPopupConfig(res.data.popup);
            setShowPopup(true);
          }
        }, 2400);
      }
    } catch (e) {
      setPopupConfig(null);
    }
  };

  const features = content?.features || {};
  const about = content?.about || {};
  const testimonials = content?.testimonials || [];
  const coming_soon = content?.coming_soon || [];
  const cta = content?.cta || { title: 'Ready to keep the show on the road?', description: 'Join HopSeat and connect with the entertainment transportation industry.', cta_text: "Let's Go" };
  const footer = content?.footer || { copyright: '2026 HopSeat. Connecting the entertainment industry with verified drivers.' };

  // Hero trust band — staffing-first credibility signals
  const trustItems = [
    { icon: ShieldCheck, label: 'Verified CDL Drivers' },
    { icon: CheckCircle, label: 'FMCSA-Checked Carriers' },
    { icon: Users, label: 'Carrier-Insured & Background-Checked Drivers' },
    { icon: Zap, label: 'Match in Minutes' },
  ];

  const curatedNavPages = (() => {
    const disallowedTitles = new Set(['pricing', 'socials & news']);
    const seen = new Set();
    return navPages.filter((p) => {
      const title = (p?.title || '').trim().toLowerCase();
      if (disallowedTitles.has(title)) return false;
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
  })();

  const navLinks = [
    { to: '/for-drivers', label: 'For Drivers', testid: 'landing-nav-for-drivers-link' },
    { to: '/for-companies', label: 'For Companies', testid: 'landing-nav-for-companies-link' },
    { to: '/for-tours', label: 'For Tour Organizers', testid: 'landing-nav-for-tms-link' },
    ...curatedNavPages.map((p) => ({ to: `/p/${p.slug}`, label: p.title, testid: `landing-nav-page-${p.slug}` })),
  ];

  return (
    <>
      <div className="hsx-landing-root min-h-screen overflow-x-hidden bg-[var(--app-bg)] text-[var(--text-primary)]">
        <div
          className="fixed left-0 top-0 z-50 h-[2px] bg-gradient-to-r from-orange-400 via-orange-500 to-amber-300"
          style={{ width: `${scrollProgress}%` }}
          data-testid="landing-scroll-progress"
        />

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="scroll-to-top-btn"
          aria-label="Scroll to top"
          style={{
            opacity: scrollY > 300 ? 1 : 0,
            transform: scrollY > 300 ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.9)',
            pointerEvents: scrollY > 300 ? 'auto' : 'none',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
          className="fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-orange-500/40 bg-[#0b0e14]/90 text-orange-400 shadow-lg shadow-orange-500/20 backdrop-blur-md hover:bg-orange-500 hover:text-white hover:border-orange-500 focus:outline-none"
        >
          <ChevronUp className="h-5 w-5" strokeWidth={2.5} />
        </button>

        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="hsx-hero-img-wrap absolute inset-0 z-0">
            <img src={HERO_BG} alt="" aria-hidden className="hsx-hero-img h-full w-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#070a12]/70 via-[#0a0f1c]/80 to-[#070a12]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_28%,rgba(249,115,22,0.12),transparent_55%)]" />
            <div className="hsx-grid-fade" />
          </div>
          <div className="hsx-radial-glow" style={{ top: '-160px', left: '50%', transform: 'translateX(-50%)' }} />
          <div className="hsx-scanline" />

          <nav className="hsx-nav sticky top-0 z-40 border-b border-white/10 bg-[#070a12]/70 backdrop-blur-xl">
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 md:px-6">
              {/* Logo */}
              <Link to="/" className="flex flex-shrink-0 items-center gap-2 sm:gap-3" data-testid="landing-nav-logo" onClick={() => setMobileMenuOpen(false)}>
                <img src="/favicon-192x192.png?v=20260609" alt="HopSeat Logo" className="h-9 w-9 object-contain sm:h-10 sm:w-10" />
                <span className="text-base font-bold tracking-tight text-white sm:text-xl md:text-2xl">HopSeat</span>
              </Link>

              {/* Desktop nav links */}
              <div className="hidden items-center gap-1 lg:flex">
                {navLinks.map((l) => (
                  <Link key={l.to} to={l.to} className="rounded-full px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white" data-testid={l.testid}>
                    {l.label}
                  </Link>
                ))}
              </div>

              {/* Desktop auth buttons */}
              <div className="hidden flex-shrink-0 items-center gap-2 lg:flex">
                {effectiveUser ? (
                  <>
                    <Link to="/dashboard" className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-orange-400/50 hover:text-white" data-testid="landing-dashboard-button">Dashboard</Link>
                    <button onClick={logout} className="hsx-cta-sheen rounded-full bg-orange-500 px-5 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(249,115,22,0.7)] transition-colors hover:bg-orange-600" data-testid="landing-logout-button">Log Out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-orange-400/50 hover:text-white" data-testid="landing-login-button">Login</Link>
                    <Link to="/register" className="hsx-cta-sheen rounded-full bg-orange-500 px-5 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(249,115,22,0.7)] transition-colors hover:bg-orange-600" data-testid="landing-get-started-button">Get Started</Link>
                  </>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white transition-colors hover:border-orange-400/40 hover:bg-white/[0.08] lg:hidden"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>

            {/* Mobile menu panel */}
            {mobileMenuOpen && (
              <div className="hsx-mobile-menu border-t border-white/10 bg-[#070a12]/95 backdrop-blur-xl lg:hidden" data-testid="mobile-menu-panel">
                <div className="mx-auto flex w-full max-w-7xl flex-col px-4 py-4">
                  {navLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium text-slate-200 transition-colors hover:bg-white/5 hover:text-white"
                      data-testid={`${l.testid}-mobile`}
                    >
                      {l.label}
                      <ArrowRight className="h-4 w-4 text-orange-300/70" />
                    </Link>
                  ))}
                  <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
                    {effectiveUser ? (
                      <>
                        <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="rounded-xl border border-white/15 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:border-orange-400/50" data-testid="landing-dashboard-button-mobile">Dashboard</Link>
                        <button onClick={() => { setMobileMenuOpen(false); logout(); }} className="rounded-xl bg-orange-500 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-orange-600" data-testid="landing-logout-button-mobile">Log Out</button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="rounded-xl border border-white/15 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:border-orange-400/50" data-testid="landing-login-button-mobile">Login</Link>
                        <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="rounded-xl bg-orange-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(249,115,22,0.7)] transition-colors hover:bg-orange-600" data-testid="landing-get-started-button-mobile">Get Started</Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </nav>

          <div className="relative z-10 mx-auto w-full max-w-4xl px-4 pt-16 pb-14 text-center md:px-6 lg:pt-24">
            <Reveal>
              <span className="hsx-tech-pill hsx-pulse-glow" data-testid="landing-hero-badge">
                <Sparkles className="h-3.5 w-3.5" />
                The tour bus staffing marketplace
              </span>
              <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="landing-hero-title">
                Post a trip. Get a verified driver.
                <span className="hsx-accent-gradient block">Move your fleet.</span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base text-slate-300 md:text-lg" data-testid="landing-hero-description">
                Fill CDL drivers for tours and festivals in minutes — connecting verified drivers with entertainment tour organizers and bus companies.
              </p>

              <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row" data-testid="landing-hero-cta-row">
                {effectiveUser ? (
                  <Link to="/dashboard" className="btn-primary hsx-cta inline-flex items-center px-8 py-4 text-base" data-testid="landing-user-dashboard-cta">
                    Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/register?role=bus_company"
                      className="hsx-cta-sheen group flex min-w-[240px] flex-col items-center justify-center rounded-xl bg-orange-500 px-8 py-4 font-bold text-white shadow-[0_12px_36px_-6px_rgba(249,115,22,0.5)] transition-transform duration-200 hover:-translate-y-1 hover:bg-orange-600"
                      data-testid="landing-cta-need-driver"
                    >
                      <span className="text-lg">I need a driver</span>
                      <span className="mt-0.5 text-xs font-medium text-orange-100/90">Operator / Company</span>
                    </Link>
                    <Link
                      to="/register?role=driver"
                      className="group flex min-w-[240px] flex-col items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] px-8 py-4 font-bold text-white backdrop-blur-md transition-[transform,border-color,background-color] duration-200 hover:-translate-y-1 hover:border-orange-400/40 hover:bg-white/[0.08]"
                      data-testid="landing-cta-im-driver"
                    >
                      <span className="text-lg">I'm a driver</span>
                      <span className="mt-0.5 text-xs font-medium text-slate-300">CDL / Contractor</span>
                    </Link>
                  </>
                )}
              </div>
            </Reveal>
          </div>

          {/* Trust band */}
          <Reveal>
            <div className="relative z-10 border-t border-white/5 bg-white/[0.02] backdrop-blur-sm">
              <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-center gap-y-3 px-4 py-5 md:px-6" data-testid="landing-trust-band">
                {trustItems.map((item, i) => (
                  <div key={item.label} className={`hsx-trust-chip flex items-center gap-2 px-4 md:px-6 ${i > 0 ? 'border-l border-white/10' : ''}`}>
                    <item.icon className="h-4 w-4 flex-shrink-0 text-orange-400" />
                    <span className="hsx-mono text-[12px] uppercase tracking-tight text-slate-200 md:text-[13px]">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </section>

        {/* ============ HOW IT WORKS ============ */}
        <section className="relative overflow-hidden py-16 md:py-20" data-testid="landing-how-it-works-section">
          <div className="hsx-grid-fade" />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
            <Reveal>
              <div className="mb-10 text-center">
                <div className="mb-3 flex items-center justify-center gap-2">
                  <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                  <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">How it works</p>
                </div>
                <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl">Staffed and on the road in three steps</h2>
              </div>
            </Reveal>
            <div className="relative grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="hsx-step-connector" />
              {HOW_IT_WORKS.map((step, idx) => (
                <Reveal key={step.title} delay={idx * 130}>
                  <div className="hsx-glass-panel hsx-glow-ring relative z-10 h-full rounded-2xl p-6" data-testid={`landing-how-step-${idx + 1}`}>
                    <div className="flex items-center justify-between">
                      <span className="hsx-icon-pulse flex h-11 w-11 items-center justify-center rounded-xl border border-orange-400/30 bg-orange-500/10">
                        <step.icon className="h-5 w-5 text-orange-300" />
                      </span>
                      <span className="hsx-mono text-2xl font-bold text-white/15">0{idx + 1}</span>
                    </div>
                    <h3 className="mt-4 text-xl font-semibold text-white">{step.title}</h3>
                    <p className="mt-2 text-sm text-slate-400">{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ============ PLATFORM PREVIEW (floating phones) ============ */}
        <PlatformPreview />

        {/* ============ AUDIENCE FEATURE ROWS ============ */}
        <section className="border-y border-white/10 bg-[#0c1426] py-16 md:py-20" data-testid="landing-audience-section">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <Reveal>
              <div className="mb-12 text-center md:max-w-3xl md:mx-auto">
                <div className="mb-3 flex items-center justify-center gap-2">
                  <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                  <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Built for everyone on the road</p>
                </div>
                <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{features.title || 'One platform, every role'}</h2>
                {features.subtitle && <p className="mt-3 text-base text-slate-400">{features.subtitle}</p>}
              </div>
            </Reveal>

            <div className="space-y-8">
              {(features.cards || []).map((card, idx) => {
                const img = AUDIENCE_IMAGES[card.id] || AUDIENCE_IMAGES.drivers;
                const link = AUDIENCE_LINKS[card.id] || '/register';
                const reverse = idx % 2 === 1;
                return (
                  <Reveal key={card.id || idx} delay={idx * 90}>
                    <div className={`hsx-glass-panel hsx-glow-ring grid grid-cols-1 items-center gap-6 rounded-2xl p-5 md:gap-10 md:p-6 lg:grid-cols-2 ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`landing-audience-row-${card.id || idx}`}>
                      <div className="group relative h-56 overflow-hidden rounded-xl ring-1 ring-white/10 lg:h-72">
                        <img src={img} alt={card.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070a12]/70 via-transparent to-transparent" />
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.16),transparent_55%)]" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">{card.title}</h3>
                        <ul className="mt-4 space-y-3">
                          {(card.items || []).slice(0, 4).map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-slate-300 md:text-base">
                              <span className="hsx-bullet-dot" />
                              {item}
                            </li>
                          ))}
                        </ul>
                        <Link to={link} className="group mt-5 inline-flex items-center text-sm font-medium text-orange-300 transition-colors hover:text-orange-200" data-testid={`landing-audience-link-${card.id || idx}`}>
                          Learn more <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ ABOUT ============ */}
        {about.title && (
          <section className="border-b border-white/10 bg-[#0d1424] py-16" data-testid="landing-about-section">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
              <Reveal>
                <div className="mb-10 text-center">
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                    <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">About</p>
                  </div>
                  <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{about.title}</h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
                <Reveal>
                  <div>
                    {about.description && <p className="text-base text-slate-300 md:text-lg">{about.description}</p>}
                    {about.description2 && <p className="mt-4 text-base text-slate-400">{about.description2}</p>}
                    <div className="mt-6">
                      <Link to="/register" className="btn-primary px-6 py-3" data-testid="landing-about-cta-link">{about.cta_text || 'Join Now'}</Link>
                    </div>
                    {about.cta_subtitle && <p className="mt-3 text-sm text-slate-400">{about.cta_subtitle}</p>}
                  </div>
                </Reveal>
                <Reveal>
                  <div className="relative overflow-hidden rounded-2xl border border-white/10">
                    <img src={about.image_url || AUDIENCE_IMAGES.companies} alt="HopSeat tour bus operations" className="h-full w-full object-cover" data-testid="landing-about-image" />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                </Reveal>
              </div>
            </div>
          </section>
        )}

        {/* ============ DIRECTORIES / MARKETPLACE PROOF ============ */}
        <ComingSoonSection items={coming_soon || []} stats={stats} />

        {/* ============ TESTIMONIALS ============ */}
        {testimonials && testimonials.length > 0 && (
          <section className="border-y border-white/10 bg-[#0e1628] py-16" data-testid="landing-testimonials-section">
            <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
              <Reveal>
                <div className="mb-10 text-center">
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                    <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Testimonials</p>
                  </div>
                  <h2 className="text-3xl font-bold text-white md:text-4xl">What operators say about HopSeat</h2>
                </div>
              </Reveal>
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
                {testimonials.map((t, idx) => (
                  <Reveal key={t.id || idx} delay={idx * 80}>
                    <article className="hsx-glass-panel hsx-glow-ring h-full rounded-2xl p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {t.image_url ? (
                            <img src={t.image_url} alt={t.author} className="h-12 w-12 rounded-full object-cover ring-2 ring-orange-400/30" />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-orange-500/20" />
                          )}
                          <p className="font-semibold text-white">{t.author}</p>
                        </div>
                        <span className="hsx-mono flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-300">
                          <CheckCircle className="h-3 w-3" /> Verified
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">“{t.quote}”</p>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ============ SOCIALS & NEWS ============ */}
        <section id="social-news" className="py-16" data-testid="landing-social-news-section">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-[#101a2d]/85 p-6 text-center">
                <div className="mb-3 flex items-center justify-center gap-2">
                  <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                  <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Socials & News</p>
                </div>
                <h3 className="text-2xl font-bold text-white">Stay synced with product drops and industry updates</h3>
                <p className="mt-2 text-sm text-slate-400">Get roadmap updates, feature launches, and tour transport insights straight to your inbox.</p>
                <div className="mt-5 mx-auto max-w-lg" data-testid="landing-subscribe-form-wrap">
                  <SubscribeForm />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ============ FINAL CTA ============ */}
        <section className="relative overflow-hidden py-16" data-testid="landing-cta-section">
          <div className="hsx-cta-aurora" />
          <div className="relative z-10 mx-auto w-full max-w-5xl px-4 text-center md:px-6">
            <Reveal>
              <div className="mb-3 flex items-center justify-center gap-2">
                <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
                <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Ready to move faster?</p>
              </div>
              <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl md:text-5xl">{cta.title}</h2>
              {cta.description && <p className="mx-auto mt-3 max-w-3xl text-slate-300 md:text-lg">{cta.description}</p>}
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link to="/register?role=bus_company" className="hsx-cta-sheen btn-primary inline-flex items-center rounded-lg px-8 py-3 text-base shadow-[0_12px_36px_-6px_rgba(249,115,22,0.5)]" data-testid="landing-final-cta-need-driver">
                  I need a driver
                </Link>
                <Link to="/register?role=driver" className="inline-flex items-center rounded-lg border border-orange-400/40 px-8 py-3 text-base font-medium text-orange-300 transition-colors hover:border-orange-300 hover:text-orange-100" data-testid="landing-final-cta-im-driver">
                  I'm a driver
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer className="relative overflow-hidden border-t border-white/10 bg-[#070b14] pt-16 pb-8">
          <div className="hsx-grid-fade" />
          <div className="hsx-radial-glow" style={{ bottom: '-260px', left: '50%', transform: 'translateX(-50%)' }} />
          {/* top hairline accent */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

          <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4">
              <div className="col-span-2 md:col-span-1">
                <div className="mb-3 flex items-center gap-2.5">
                  <img src="/favicon-192x192.png?v=20260609" alt="HopSeat logo" className="h-8 w-8 rounded-lg object-contain" />
                  <span className="text-lg font-extrabold tracking-tight text-white">Hop<span className="text-orange-400">Seat</span></span>
                </div>
                <p className="mb-4 max-w-[220px] text-xs leading-relaxed text-slate-500">
                  The workforce &amp; logistics platform for the entertainment transportation industry.
                </p>
                <p className="hsx-mono text-[10px] uppercase tracking-[0.18em] text-orange-400/70">Built for the road</p>
              </div>

              <div>
                <p className="hsx-mono mb-4 text-[10px] uppercase tracking-[0.2em] text-orange-400/80">Platform</p>
                <ul className="space-y-2.5">
                  {[
                    { to: '/for-drivers', label: 'For Drivers' },
                    { to: '/for-companies', label: 'For Companies' },
                    { to: '/for-tours', label: 'For Tour Organizers' },
                    { to: '/membership', label: 'Membership Plans' },
                    { to: '/advertise', label: 'Advertise' },
                    { to: '/green-room', label: 'The Driver Bay' },
                    { to: '/ideas', label: 'Idea Board' },
                    { to: '/updates', label: "What's New" },
                  ].map(({ to, label }) => (
                    <li key={to}>
                      <Link to={to} className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid={`landing-footer-link${to}`}>{label}</Link>
                    </li>
                  ))}
                  {curatedNavPages.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/p/${p.slug}`} className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid={`landing-footer-page-${p.slug}`}>
                        {p.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="hsx-mono mb-4 text-[10px] uppercase tracking-[0.2em] text-orange-400/80">Contact</p>
                <ul className="space-y-2.5">
                  <li><a href="mailto:support@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-support-link">Support</a></li>
                  <li><a href="mailto:press@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-press-link">Press</a></li>
                  <li><a href="mailto:partnerships@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-partnerships-link">Partnerships</a></li>
                  <li><a href="mailto:investors@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-investors-link">Investors</a></li>
                  <li><a href="mailto:legal@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-legal-link">Legal Inquiries</a></li>
                  <li><a href="mailto:privacy@hopseat.app" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-privacy-email-link">Privacy Requests</a></li>
                </ul>
              </div>

              <div>
                <p className="hsx-mono mb-4 text-[10px] uppercase tracking-[0.2em] text-orange-400/80">Legal</p>
                <ul className="space-y-2.5">
                  <li><Link to="/terms" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-terms-link">Terms of Use</Link></li>
                  <li><Link to="/privacy" className="text-xs text-slate-400 transition-colors hover:text-orange-300" data-testid="landing-footer-privacy-link">Privacy Policy</Link></li>
                  <li><Link to="/compliance" className="text-xs text-slate-400 transition-colors hover:text-orange-300">Compliance</Link></li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row" data-testid="landing-footer">
              <p>&copy; {footer.copyright}</p>
              <p className="hsx-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">Built for the road. Built for the show.</p>
            </div>
          </div>
        </footer>
      </div>

      {showPopup && popupConfig && (
        <SitePopup
          config={popupConfig}
          onClose={() => {
            setShowPopup(false);
            setPopupConfig(null);
          }}
        />
      )}
    </>
  );
};

const ComingSoonSection = ({ items, stats }) => {
  const [expandedId, setExpandedId] = useState(null);

  // Secondary proof: the platform's data/tools that benefit drivers (rounded for marketing)
  const platformStats = [
    stats?.bus_parking ? { value: `${Math.floor(stats.bus_parking / 100) * 100}+`, label: 'Bus Parking Locations' } : null,
    stats?.venues ? { value: `${stats.venues.toLocaleString()}+`, label: 'Venues Mapped' } : null,
    stats?.airports ? { value: `${stats.airports}+`, label: 'Airports Indexed' } : null,
    stats?.bus_mechanics ? { value: `${stats.bus_mechanics}+`, label: 'Bus Mechanics' } : null,
  ].filter(Boolean);

  const launchRhythmItems = [
    'Weekly product updates for core workflows',
    'Monthly capability drops for operations and billing',
    'Role-specific improvements driven by live user feedback',
    'Roadmap highlights shared with each launch wave',
  ];

  const FALLBACK_FEATURES = [
    { id: 'bus_mechanics', title: 'Bus Mechanics Directory', subtitle: 'Mobile and shop mechanics', icon: 'wrench', color: 'orange', status: 'live', description: 'Find certified bus mechanics near you — mobile and shop-based.', features: ['Mobile mechanic locator', 'Shop ratings and reviews', 'Emergency roadside contacts'] },
    { id: 'airports', title: 'Airports Directory', subtitle: '149 airports with bus access data', icon: 'plane', color: 'purple', status: 'live', description: 'Every major airport indexed with bus staging areas, load-in access, and parking info.', features: ['Bus staging locations', 'Load-in/load-out access points', 'Parking and overnight info'] },
    { id: 'bus_washes', title: 'Bus Washes Directory', subtitle: 'Bus wash locations across North America', icon: 'droplets', color: 'orange', status: 'live', description: 'Locate bus wash facilities on your route.', features: ['Full-size bus wash locations', 'Pricing and hours', 'Route-optimized search'] },
    { id: 'bus_parking', title: 'Bus Parking Directory', subtitle: '200+ bus & RV parking locations', icon: 'map-pin', color: 'blue', status: 'live', description: 'Find safe, legal parking for tour buses and motorcoaches.', features: ['200+ verified locations', 'Overnight parking availability', 'Security and amenity details'] },
    { id: 'venues', title: 'Venues Database', subtitle: '2,762 venues with load-in details', icon: 'building', color: 'orange', status: 'live', description: 'The most comprehensive venue database built for tour bus operations.', features: ['2,762+ venues mapped', 'Load-in dock locations', 'Bus access and staging info'] },
    { id: 'analytics', title: 'Advanced Analytics', subtitle: 'Performance and operational analytics', icon: 'bar-chart', color: 'blue', status: 'live', description: 'Track your performance, earnings trends, and operational metrics.', features: ['Gig completion stats', 'Earnings and payout history', 'Points and tier progress'] },
    { id: 'pay_disputes', title: 'Pay Dispute System', subtitle: 'Formal pay dispute resolution', icon: 'scale', color: 'orange', status: 'live', description: 'Structured dispute resolution with evidence uploads and admin arbitration.', features: ['72-hour response windows', 'Evidence upload system', 'Admin arbitration and resolution'] },
    { id: 'master_tour', title: 'MasterTour Integration', subtitle: 'Sync tours from Eventric MasterTour', icon: 'clock', color: 'orange', status: 'live', description: 'Connect your Eventric MasterTour account to sync show dates, travel days, and venue details.', features: ['OAuth 1.0 secure connection', 'Calendar sync for availability', 'Venue and city auto-import'] },
    { id: 'priority_gig_alerts', title: 'Priority Gig Alerts', subtitle: 'First access to new gig postings', icon: 'zap', color: 'orange', status: 'coming_soon', description: 'Get notified first when new gigs matching your profile are posted.', features: ['Push notifications for matching gigs', 'Priority placement in applications', 'Custom alert preferences'] },
  ];

  const allFeatures = items && items.length > 0 ? items : FALLBACK_FEATURES;
  const seen = new Set();
  const uniqueFeatures = allFeatures.filter(f => { if (seen.has(f.id)) return false; seen.add(f.id); return true; });
  const liveItems = uniqueFeatures.filter(i => i.status === 'live');
  const upcomingItems = uniqueFeatures.filter(i => i.status !== 'live');

  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#0a1220] py-16" data-testid="landing-coming-soon-section">
      <div className="hsx-grid-fade" />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 md:px-6">
        <Reveal>
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center gap-2">
              <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
              <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Beyond staffing</p>
            </div>
            <h2 className="mt-2 text-3xl font-bold text-white md:text-4xl">More than a gig board — a full driver toolkit</h2>
            <p className="mt-2 mx-auto max-w-3xl text-slate-400">
              HopSeat fills driver schedules first. On top of that, every membership unlocks the operations tools and directories that make life on the road easier — venues, parking, airports, and more for the entertainment tour bus world.
            </p>
          </div>
        </Reveal>

        {platformStats.length > 0 && (
          <Reveal>
            <div className="mb-8 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]" data-testid="landing-platform-stats-band">
              {platformStats.map((s) => (
                <div key={s.label} className="hsx-glass-panel rounded-xl p-4 text-center">
                  <p className="hsx-trust-stat hsx-mono text-2xl font-bold">{s.value}</p>
                  <p className="mt-1 text-xs text-slate-400">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        <Reveal>
          <div className="mb-5 rounded-xl border border-orange-400/20 bg-orange-500/10 p-4" data-testid="roadmap-launch-rhythm-card">
            <div className="mb-2 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-orange-300" />
              <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">Launch Rhythm</p>
            </div>
            <ul className="space-y-2 text-sm text-slate-200" data-testid="roadmap-launch-rhythm-list">
              {launchRhythmItems.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2" data-testid={`roadmap-launch-rhythm-item-${idx + 1}`}>
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-300" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {liveItems.length > 0 && (
          <div className="space-y-3 mb-6" data-testid="landing-live-features-list">
            {liveItems.map((item, idx) => {
              const IconComp = iconMap[item.icon] || Clock;
              const isExpanded = expandedId === item.id;
              return (
                <Reveal key={item.id || idx}>
                  <div className="rounded-xl border border-green-500/30 bg-white/[0.03] shadow-green-500/20 shadow-lg">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl p-4 text-left transition-colors hover:bg-white/[0.03]"
                      data-testid={`landing-feature-toggle-${item.id || idx}`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-white/15 bg-black/30">
                          {item.id === 'master_tour' ? (
                            <MasterTourLogo size={28} />
                          ) : (
                            <IconComp className="h-5 w-5 text-green-300" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{item.title}</p>
                          <p className="truncate text-xs text-slate-400">{item.subtitle}</p>
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <span className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 px-2.5 py-1 text-[10px] font-semibold text-white">
                          Live
                        </span>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-300" /> : <ChevronDown className="h-4 w-4 text-slate-300" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-white/10 px-4 pb-4 pt-3">
                        <p className="text-sm text-slate-300">{item.description}</p>
                        {item.features && item.features.length > 0 && (
                          <ul className="mt-3 space-y-1.5">
                            {item.features.map((feat, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-400" />
                                {feat}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        {upcomingItems.length > 0 && (
          <div className="space-y-3" data-testid="landing-coming-soon-list">
            {upcomingItems.map((item, idx) => {
            const IconComp = iconMap[item.icon] || Clock;
            const isExpanded = expandedId === item.id;

            return (
              <Reveal key={item.id || idx}>
                <div className="rounded-xl border border-orange-500/30 bg-white/[0.03] shadow-orange-500/20 shadow-lg">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    className="flex w-full items-center justify-between gap-3 p-4 text-left"
                    data-testid={`landing-coming-soon-toggle-${item.id || idx}`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-white/15 bg-black/30">
                        {item.id === 'master_tour' || (item.title && item.title.toLowerCase().includes('master tour')) ? (
                          <MasterTourLogo size={28} />
                        ) : (
                          <IconComp className="h-5 w-5 text-orange-300" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{item.title}</p>
                        <p className="truncate text-xs text-slate-400">{item.subtitle}</p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className="rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-2 py-1 text-[10px] font-semibold text-white">
                        Coming Soon
                      </span>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-300" /> : <ChevronDown className="h-4 w-4 text-slate-300" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-white/10 px-4 pb-4 pt-3">
                      <p className="text-sm text-slate-300">{item.description}</p>
                      {item.features && item.features.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {item.features.map((feat, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                              <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-300" />
                              {feat}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </Reveal>
            );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingExperience;
